import { useState } from 'react';

import StorytellerUtils from 'lib/StorytellerUtils';
import { I18n } from 'common/i18n';
import { ComponentMeasureContainer } from './component-measure-container';
import { Provider } from 'react-redux';
import { StorytellerReduxStoreFactory } from 'store/StorytellerReduxStore';
import { type AnyMeasureBlockComponent, type BlockComponent, ComponentType } from 'types';
import Constants from 'lib/Constants';

import { type ReactRendererProps } from '../../shared/ComponentRegistry';
import VizMessageError from 'common/components/VizMessageError';

export const resizableProps = (componentData: BlockComponent) => {
  return {
    resizeSupported: componentData.type === ComponentType.MEASURE_CHART,
    resizeOptions: {
      minHeight: Constants.MINIMUM_COMPONENT_HEIGHTS_PX.VISUALIZATION,
    },
    defaultHeight: Constants.DEFAULT_VISUALIZATION_HEIGHT,
  };
};

/**
 * React component renderer for measure components.
 */
export const ComponentMeasure = (props: ReactRendererProps) => {
  const { componentData, additionalFilters, blockId, componentIndex } = props;
  const [hasError, setHasError] = useState(false);
  const typeClasses = StorytellerUtils.typeToClassesForComponentType(componentData.type);

  // Added double checking as sometimes isInsitu prop is inside the value prop but the
  // AnyMeasureBlockComponent says the opposite.
  const value = componentData?.value;
  const isInsitu =
    ('isInsitu' in componentData && componentData.isInsitu) ||
    (value && 'isInsitu' in value && value.isInsitu);

  const componentType =
    componentData.type === ComponentType.MEASURE_CARD
      ? ComponentType.MEASURE_CARD
      : ComponentType.MEASURE_CHART;

  const anyMeasureBlockComponentData: AnyMeasureBlockComponent = {
    ...componentData,
    isInsitu,
    value: componentData.value,
    type: componentType,
  };

  return (
    <div className={`component ${typeClasses} ${hasError ? 'load-error' : ''}`}>
      <div className="component-content">
        <Provider store={StorytellerReduxStoreFactory()}>
          <ComponentMeasureContainer
            resizeSupported={componentData.type === ComponentType.MEASURE_CHART}
            resizeOptions={{
              minHeight: Constants.MINIMUM_COMPONENT_HEIGHTS_PX.VISUALIZATION,
            }}
            defaultHeight={Constants.DEFAULT_VISUALIZATION_HEIGHT}
            errorCallback={setHasError}
            componentData={anyMeasureBlockComponentData}
            additionalFilters={additionalFilters}
            blockId={blockId}
            componentIndex={componentIndex}
          />
        </Provider>
      </div>
      <div className="component-error">
        <VizMessageError
          type="privateOrDeletedAsset"
          errorMessage={I18n.t('editor.components.performance_measures.generic_error')}
        />
      </div>
    </div>
  );
};
